import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useGetTechnicalSupportFeedbackFormReportQuery } from '../../../../api/endpoints/technicalSupportRequest';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../../utils/filterUtils';
import { formatDateForApi } from '../../../../utils/dateUtils';

export const backendColumnMap = {
    technicalSupportId: 'TechnicalSupportId',
    dealerName: 'DealerName',
    technicalResponsible: 'TechnicalResponsible',
    question: 'Question',
    rating: 'Rating',
    comment: 'Comment',
    answerDate: 'AnswerDate'
};

export const DROPDOWN_FIELDS = ['dealerName', 'technicalResponsible', 'question', 'rating'];

export const TEXT_FIELDS = ['technicalSupportId', 'comment'];

export const DATE_FIELDS = ['answerDate'];

export const USER_DROPDOWN_FIELDS = ['technicalResponsible'];

const BACKEND_OPTION_FIELDS = DROPDOWN_FIELDS.filter((field) => !USER_DROPDOWN_FIELDS.includes(field));

const FILTER_OPTIONS_COLUMNS = BACKEND_OPTION_FIELDS.map((field) => backendColumnMap[field]);

const CONTAINS_DROPDOWN_FIELDS = ['technicalResponsible'];

const OPERATOR_EQUALS = 5;

const OPERATOR_CONTAINS = 1;

const INITIAL_LAZY_PARAMS = { first: 0, rows: 10, page: 1 };

const INITIAL_FILTER_STATE = {
    ...TEXT_FIELDS.reduce((acc, field) => {
        acc[field] = null;
        acc[`${field}MatchMode`] = null;
        return acc;
    }, {}),
    ...DATE_FIELDS.reduce((acc, field) => {
        acc[field] = null;
        acc[`${field}MatchMode`] = null;
        return acc;
    }, {}),
    ...DROPDOWN_FIELDS.reduce((acc, field) => {
        acc[field] = null;
        return acc;
    }, {})
};

const getOperatorValue = (matchMode, fallbackMatchMode) => matchModeToOperator[matchMode] ?? matchModeToOperator[fallbackMatchMode];

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    TEXT_FIELDS.forEach((field) => {
        const value = filtersState?.[field];
        if (value === null || value === undefined || String(value).trim() === '') return;
        filters.push({
            column: backendColumnMap[field],
            operator: getOperatorValue(filtersState?.[`${field}MatchMode`], FilterMatchMode.CONTAINS),
            value: String(value).trim()
        });
    });

    DROPDOWN_FIELDS.forEach((field) => {
        const value = filtersState?.[field];
        if (value === null || value === undefined || value === '') return;
        filters.push({
            column: backendColumnMap[field],
            operator: CONTAINS_DROPDOWN_FIELDS.includes(field) ? OPERATOR_CONTAINS : OPERATOR_EQUALS,
            value: String(value)
        });
    });

    DATE_FIELDS.forEach((field) => {
        const formatted = formatDateForApi(filtersState?.[field]);
        if (!formatted) return;
        filters.push({
            column: backendColumnMap[field],
            operator: getOperatorValue(filtersState?.[`${field}MatchMode`], FilterMatchMode.DATE_IS),
            value: formatted
        });
    });

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = backendColumnMap[field];
    if (!column) return null;
    return [{ column, direction: order === 1 ? 1 : 2 }];
};

const useFeedbackFormReport = () => {
    const { getCurrentLanguageId } = useLanguage();

    const [lazyParams, setLazyParams] = useState(INITIAL_LAZY_PARAMS);
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);
    const [filterOptions, setFilterOptions] = useState(null);
    const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);

    const queryParams = useMemo(() => {
        const filters = buildDynamicFilters(activeFilters);
        const sorts = buildDynamicSorts(sortField, sortOrder);

        return {
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            languageId: getCurrentLanguageId(),
            filters: filters.length > 0 ? filters : null,
            sorts: sorts && sorts.length > 0 ? sorts : null,
            includeFilterOptions: !filterOptionsLoaded,
            filterOptionsColumns: !filterOptionsLoaded ? FILTER_OPTIONS_COLUMNS : null
        };
    }, [lazyParams.page, lazyParams.rows, activeFilters, sortField, sortOrder, filterOptionsLoaded, getCurrentLanguageId]);

    const exportParams = useMemo(() => {
        const filters = buildDynamicFilters(activeFilters);
        const sorts = buildDynamicSorts(sortField, sortOrder);

        return {
            filters: filters.length > 0 ? filters : null,
            sorts: sorts && sorts.length > 0 ? sorts : null,
            compressionType: 0
        };
    }, [activeFilters, sortField, sortOrder]);

    const { data, isLoading, isFetching } = useGetTechnicalSupportFeedbackFormReportQuery(queryParams);

    useEffect(() => {
        if (!filterOptionsLoaded && data?.filterOptions) {
            setFilterOptions(data.filterOptions);
            setFilterOptionsLoaded(true);
        }
    }, [data, filterOptionsLoaded]);

    const handleFilterChange = useCallback((updatedFilters) => {
        setActiveFilters((prev) => ({ ...prev, ...updatedFilters }));
        setLazyParams(INITIAL_LAZY_PARAMS);
    }, []);

    const initFilters = useCallback(() => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setSortField(null);
        setSortOrder(0);
        setLazyParams(INITIAL_LAZY_PARAMS);
    }, []);

    const handleSortChange = useCallback((event) => {
        const nextSortField = event?.sortField || null;
        const nextSortOrder = event?.sortOrder || 0;

        if (nextSortOrder !== 0 && !backendColumnMap[nextSortField]) return;

        setSortField(nextSortField);
        setSortOrder(nextSortOrder);
        setLazyParams(INITIAL_LAZY_PARAMS);
    }, []);

    const onPage = useCallback((event) => {
        setLazyParams({ first: event.first, rows: event.rows, page: event.page + 1 });
    }, []);

    return {
        items: Array.isArray(data?.items) ? data.items : [],
        totalCount: data?.totalCount ?? 0,
        isLoading: isLoading || isFetching,
        lazyParams,
        activeFilters,
        filterOptions,
        sortField,
        sortOrder,
        exportParams,
        onPage,
        onSort: handleSortChange,
        handleFilterChange,
        initFilters
    };
};

export default useFeedbackFormReport;
